import React, {useState, useMemo, useCallback, useEffect} from 'react';
import validator from 'validator';
import Select from 'react-select';
import {connect} from 'react-redux';
import {
  Modal,
  PrimaryButton,
  DropdownIndicator,
  customStyles,
} from '../../components';
import {
  performVerification,
  fetchStates,
  fetchLgas,
} from '../../requests/queries/address-verification';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {toast} from 'react-toastify';
import * as Actions from '../../store/actions';

const GENDER = [
  {value: 'Male', label: 'Male'},
  {value: 'Female', label: 'Female'},
];

const initialState = {
  app_id: '',
  first_name: '',
  last_name: '',
  mobile: '',
  street: '',
  lga: null,
  state: null,
  landmark: '',
  dob: '',
  gender: null,
};

function VerificationFormModal({
  open,
  setOpen,
  apps: {allApps},
  onVerificationSuccess,
}) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(initialState);
  const [selectedApps, setSelectedApps] = useState(null);

  const {data: states = [], isLoading: isLoadingStates} = useQuery(
    'states',
    fetchStates,
    {
      enabled: open,
      onError: error => {
        toast.error(`Failed to fetch states: ${error.message}`);
        console.error('State fetch error:', error);
      },
    },
  );

  const {data: lgas = [], isLoading: isLoadingLgas} = useQuery(
    ['lgas', formData.state?.value],
    () => fetchLgas(formData.state.value),
    {
      enabled: !!formData.state?.value,
      onError: error => {
        toast.error(`Failed to fetch LGAs: ${error.message}`);
        console.error('LGA fetch error:', error);
      },
    },
  );

  useEffect(() => {
    if (formData.state?.value) {
      setFormData(prev => ({...prev, lga: null}));
    }
  }, [formData.state]);

  const userApps = useMemo(
    () =>
      allApps &&
      allApps?.apps?.map(app => ({
        label: app.name,
        value: app._id,
        name: app.name,
      })),
    [allApps],
  );

  const PHONE_NUMBER_LENGTH = 13;
  const phoneNumber =
    formData.mobile?.length > PHONE_NUMBER_LENGTH
      ? formData.mobile?.replace('+', '')?.slice(0, 3) +
        formData.mobile?.replace('+', '')?.slice(4)
      : formData.mobile;

  const isValidPhoneNumber =
    phoneNumber && validator.isMobilePhone(phoneNumber, 'en-NG');

  const isNotEmpty =
    formData.app_id &&
    formData.first_name &&
    formData.last_name &&
    formData.street &&
    formData.lga &&
    formData.state;

  const {isLoading, mutate} = useMutation(data => performVerification(data));

  const handleInputChange = e => {
    const {value, name} = e.target;
    setFormData({...formData, [name]: value});
  };

  const handleSelectChange = (value, name) => {
    setFormData({...formData, [name]: value});
  };

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (isNotEmpty) {
        if (!isValidPhoneNumber)
          return toast.error('Please enter a valid phone number');

        const {mobile, landmark, state, gender, dob, lga, ...dataToSend} =
          formData;

        if (mobile) {
          dataToSend.mobile = phoneNumber;
        }

        if (landmark) {
          dataToSend.landmark = landmark;
        }

        if (state) {
          dataToSend.state = state.value;
        }

        if (gender) {
          dataToSend.gender = gender.value;
        }

        if (dob) {
          dataToSend.dob = dob;
        }

        if (lga) {
          dataToSend.lga = lga.value;
        }

        await mutate(dataToSend, {
          onSuccess: () => {
            toast.success('Verification successful');
            setOpen(false);
            setFormData(initialState);
            queryClient.invalidateQueries(['address-verifications']);
            if (onVerificationSuccess) {
              onVerificationSuccess();
            }
          },
          onError: error => {
            toast.error(error.response?.data?.error || 'Verification failed');
          },
        });
      } else {
        toast.error('Please fill all the non-optional fields');
      }
    },
    [
      formData,
      isNotEmpty,
      isValidPhoneNumber,
      mutate,
      onVerificationSuccess,
      phoneNumber,
      queryClient,
      setOpen,
    ],
  );

  return (
    <Modal
      show={open}
      onClose={setOpen}
      extraModalClasses="max-w-[480px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
      modalTitle="Submit Address"
    >
      <form onSubmit={handleSubmit}>
        <fieldset>
          <div className="px-6">
            <label htmlFor="first_name" className="mt-6">
              <span className="text-sm">First name</span>
              <input
                type="text"
                id="first_name"
                className="mt-2"
                onChange={handleInputChange}
                name="first_name"
              />
            </label>
            <label htmlFor="last_name" className="mt-6">
              <span className="text-sm">Last name</span>
              <input
                type="text"
                id="last_name"
                className="mt-2"
                onChange={handleInputChange}
                name="last_name"
              />
            </label>
            <label htmlFor="mobile" className="mt-6">
              <span className="text-sm">Phone number (Optional)</span>
              <input
                type="tel"
                id="mobile"
                className="mt-2"
                onChange={handleInputChange}
                name="mobile"
              />
            </label>
            <label htmlFor="street" className="mt-6">
              <span className="text-sm">Street</span>
              <input
                type="text"
                id="street"
                className="mt-2"
                onChange={handleInputChange}
                name="street"
              />
            </label>
            <label htmlFor="state" className="relative mt-6 mb-2">
              State
            </label>
            <Select
              value={formData.state}
              onChange={value => handleSelectChange(value, 'state')}
              options={states}
              placeholder={
                isLoadingStates ? 'Loading states...' : 'Select state'
              }
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              styles={customStyles}
              className="text-start"
              menuPlacement="auto"
              isDisabled={isLoadingStates}
            />
            <label htmlFor="lga" className="relative mt-6 mb-2">
              LGA
            </label>
            <Select
              value={formData.lga}
              onChange={value => handleSelectChange(value, 'lga')}
              options={lgas}
              placeholder={isLoadingLgas ? 'Loading LGAs...' : 'Select LGA'}
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              styles={customStyles}
              className="text-start"
              menuPlacement="auto"
              isDisabled={isLoadingLgas || !formData.state}
            />
            <label htmlFor="landmark" className="mt-6">
              <span className="text-sm">Landmark (Optional)</span>
              <input
                type="text"
                id="landmark"
                className="mt-2"
                onChange={handleInputChange}
                name="landmark"
              />
            </label>
            <label htmlFor="dob" className="mt-6">
              <span className="text-sm">Date of birth (Optional)</span>
              <input
                type="date"
                name="dob"
                className="mt-2"
                onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]}
              />
            </label>
            <label htmlFor="flow_app" className="mt-6 mb-2 relative">
              Select app
            </label>
            <Select
              value={selectedApps}
              onChange={data => {
                setSelectedApps(data);
                setFormData({...formData, app_id: data?.value});
              }}
              options={userApps}
              placeholder="Select an app"
              classNamePrefix="react-select"
              components={{DropdownIndicator}}
              styles={customStyles}
              className="text-start"
              menuPlacement="auto"
            />
            <label htmlFor="gender" className="relative mt-6 mb-2">
              Gender (Optional)
            </label>
            <Select
              value={formData.gender}
              onChange={value => handleSelectChange(value, 'gender')}
              options={GENDER}
              placeholder="Select gender"
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              styles={customStyles}
              className="text-start"
              menuPlacement="auto"
            />
          </div>
          <div className="w-full mt-6 px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
            <PrimaryButton
              buttonText="Submit Address"
              className="w-full !h-10"
              loading={isLoading}
              aria-disabled={isLoading}
              type="submit"
            />
          </div>
        </fieldset>
      </form>
    </Modal>
  );
}

export default connect(state => state, Actions)(VerificationFormModal);
